import { giphyBlack, giphyDarkestGrey, giphyGreen, giphyLightGrey, giphyRed, giphyYellow } from '@giphy/js-brand'
import { Button } from '@giphy/react-giphy-brand'
import dateFormat from 'dateformat'
import React, { useContext, useState } from 'react'
import MessageContext from 'shared/components/context/message'
import UserContext from 'shared/components/context/user'
import DotsLoader from 'shared/components/loaders/dots'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import styled from 'styled-components'
import Modal from '../shared/components/modal'
import { ICapture, IPending } from '../shared/types'
import API from '../shared/utils/api'

const api = new API()

const ROW_HEIGHT = 80

const Placeholder = styled.div`
    margin-top: 30px;
    height: 400px;
    background-image: url('/static/img/supercut/placeholder-capture-table.svg');
    position: relative;

    &:after {
        content: '';
        background: linear-gradient(0deg, ${giphyBlack} 0%, rgba(0, 0, 0, 0) 100%);
        position: absolute;
        height: 100px;
        right: 0;
        bottom: 0;
        left: 0;
    }
`

const Heading = styled.div`
    font-size: 18px;
    letter-spacing: 0.26px;
    font-weight: bold;
    margin-top: 30px;
    margin-bottom: 15px;
`

const Count = styled.span`
    font-weight: normal;
`

const Delete = styled.div`
    display: none;
    color: ${giphyRed};
    vertical-align: top;
    margin-left: 16px;
`

const CaptureRow = styled.a`
    display: flex;
    height: ${ROW_HEIGHT}px;
    background-color: ${giphyDarkestGrey};
    margin: 5px 0 5px 0;

    &:hover ${Delete} {
        display: inline-block;
    }
`

const Thumbnail = styled.div<{ src: string }>`
    height: ${ROW_HEIGHT}px;
    width: ${ROW_HEIGHT}px;
    background-image: url(${props => props.src});
    background-size: cover;
    background-repeat: no-repeat;
`

const TitleContainer = styled.div`
    flex-grow: 2;
    margin: 0 35px;
    height: ${ROW_HEIGHT}px;
    line-height: ${ROW_HEIGHT}px;
    width: 0%;
`

const Title = styled.div`
    display: inline-block;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: calc(100% - 32px);
`

const DetailsContainer = styled.div`
    text-align: right;
    & > * {
        margin-right: 30px;
    }
`

const Detail = styled.div`
    display: inline-block;
    height: ${ROW_HEIGHT}px;
    line-height: ${ROW_HEIGHT}px;
    text-align: center;
    width: 100px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    vertical-align: middle;
`

const Status = styled.div<{ status: string }>`
    display: inline-block;
    color: ${giphyBlack};
    font-weight: bold;
    font-size: 13px;
    height: 34px;
    line-height: 34px;
    padding: 0 20px 0 20px;
    border-radius: 8px;
    text-transform: capitalize;

    background-color: ${props => (props.status === 'DONE' ? giphyGreen : giphyYellow)}};
`

const DeleteMessage = styled.div`
    text-align: center;
    padding: 80px 0;
`

const DeleteText = styled.div`
    margin-top: 25px;
    font-size: 20px;
    font-weight: bold;
`

const DeleteButtons = styled.div`
    margin-bottom: 50px;
    text-align: center;
`

const ConfirmButton = styled(Button)`
    display: block;
    margin: 0 auto 15px auto;
    width: 500px;
    background-color: ${giphyRed};
`

const CancelButton = styled(Button)`
    display: block;
    margin: auto;
    background-color: ${giphyDarkestGrey};
    color: ${giphyLightGrey};
`

const NotificationInfo = styled.span`
    float: right;
    color: ${giphyLightGrey};
    font-style: italic;
    font-size: 13px;
`

type Props = {
    captures: ICapture[]
    pending: IPending[]
    onCaptureDeleted?: () => void
}

const CaptureTable = ({ captures, pending, onCaptureDeleted = () => {} }: Props) => {
    const [deleteConfirming, setDeleteConfirming] = useState<ICapture>()
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [pendingDeletionID, setPendingDeletionID] = useState('')

    const { sendMessage } = useContext(MessageContext)
    const { user } = useContext(UserContext)

    const deleteClicked = (evt, capture: ICapture) => {
        evt.preventDefault()
        setDeleteConfirming(capture)
        setPendingDeletionID(capture.id)
    }

    const confirmDelete = async () => {
        googleAnalyticsEvent({
            category: 'Super Cut',
            action: 'Delete Capture',
            label: pendingDeletionID,
        })
        if (deleteConfirming) {
            setDeleteLoading(true)
            await api.deleteCapture(deleteConfirming!.id)
            setDeleteLoading(false)
            setDeleteConfirming(undefined)
            sendMessage('Capture Deleted!')
            onCaptureDeleted()
        }
    }

    const cancelDelete = () => {
        setDeleteConfirming(undefined)
    }

    if (captures.length === 0 && pending.length === 0) {
        return <Placeholder />
    } else {
        return (
            <div>
                {deleteConfirming !== undefined && (
                    <Modal onClickOutside={cancelDelete}>
                        <DeleteMessage>
                            {deleteLoading ? (
                                <>
                                    <DotsLoader />
                                    <DeleteText>Deleting...</DeleteText>
                                </>
                            ) : (
                                <DeleteText>Are you sure you want to delete this capture?</DeleteText>
                            )}
                        </DeleteMessage>
                        <DeleteButtons>
                            <ConfirmButton disabled={deleteLoading} onClick={confirmDelete}>
                                Delete
                            </ConfirmButton>
                            <CancelButton disabled={deleteLoading} onClick={cancelDelete}>
                                Cancel
                            </CancelButton>
                        </DeleteButtons>
                    </Modal>
                )}

                <Heading>
                    Pending <Count>({pending.length})</Count>
                    <NotificationInfo>
                        {
                            "If you've enabled Browser Notifications, you'll receive an update when processing is completed."
                        }
                    </NotificationInfo>
                </Heading>

                <div>
                    {pending.map((p: IPending, i: number) => (
                        <CaptureRow key={i}>
                            <TitleContainer>
                                <Title>{p.title}</Title>
                            </TitleContainer>

                            <DetailsContainer>
                                <Detail>{p.source}</Detail>
                                <Status status={p.status}>{p.status.toLowerCase()}</Status>
                            </DetailsContainer>
                        </CaptureRow>
                    ))}
                </div>

                <Heading>
                    Captures <Count>({captures.length})</Count>
                </Heading>

                <div>
                    {captures.map((c: ICapture) => (
                        <CaptureRow key={c.id} href={`/supercut/detail/${c.id}`}>
                            <Thumbnail src={c.thumbnail} />
                            <TitleContainer>
                                <Title>{c.title}</Title>
                                <Delete onClick={evt => deleteClicked(evt, c)}>
                                    <span className="ss-trash" />
                                </Delete>
                            </TitleContainer>
                            <DetailsContainer>
                                <Detail>{dateFormat(new Date(c.date), 'mmm dS, yyyy')}</Detail>
                                {user.isStaff && <Detail>{c.username}</Detail>}
                                <Detail>{c.source}</Detail>
                                <Detail>{c.segments_count} GIFs</Detail>
                                <Status status={c.status}>{c.status.toLowerCase()}</Status>
                            </DetailsContainer>
                        </CaptureRow>
                    ))}
                </div>
            </div>
        )
    }
}

export default CaptureTable
